import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
};

export const useLocationProfile = (locationId) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const latestLoadId = useRef(0);
  const currentLocationId = useRef(locationId);
  const [state, setState] = useState({
    loading: true,
    error: '',
    location: null,
    activeIssues: [],
    contacts: [],
    ticketLinks: [],
    networkSummary: null,
    assets: [],
    sectionErrors: {}
  });
  currentLocationId.current = locationId;

  const load = useCallback(async () => {
    if (!locationId) return;
    if (locationId !== currentLocationId.current) return;
    const loadId = latestLoadId.current + 1;
    latestLoadId.current = loadId;
    setState(prev => Object.assign({}, prev, { loading: true, error: '' }));
    try {
      const response = await authenticatedFetch(`/api/locations/profiles/${locationId}`);
      const data = await readJson(response);
      if (!response.ok) throw new Error(data.error || 'Failed to load location');
      if (loadId !== latestLoadId.current || locationId !== currentLocationId.current) return;
      setState({
        loading: false,
        error: '',
        location: data.location,
        activeIssues: data.activeIssues || [],
        contacts: data.contacts || [],
        ticketLinks: data.ticketLinks || [],
        networkSummary: data.networkSummary || null,
        assets: data.assets || [],
        sectionErrors: data.sectionErrors || {}
      });
    } catch (error) {
      if (loadId === latestLoadId.current && locationId === currentLocationId.current) throw error;
    }
  }, [authenticatedFetch, locationId]);

  useEffect(() => {
    load().catch(error => setState(prev => Object.assign({}, prev, { loading: false, error: error.message })));
  }, [load]);

  const reload = useCallback(async () => {
    try {
      await load();
    } catch (error) {
      setState(prev => Object.assign({}, prev, { loading: false, error: error.message }));
    }
  }, [load]);

  const runMutation = useCallback(async (url, options, fallbackMessage) => {
    const response = await authenticatedFetch(url, options);
    const data = await readJson(response);
    if (!response.ok) throw new Error(data.error || fallbackMessage);
    await reload();
    return data;
  }, [authenticatedFetch, reload]);

  const runJsonMutation = useCallback(async (url, method, payload, fallbackMessage) => runMutation(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {})
  }, fallbackMessage), [runMutation]);

  const updateProfile = useCallback(async (payload) => runJsonMutation(
    `/api/locations/profiles/${locationId}`,
    'PUT',
    payload,
    'Failed to update location profile'
  ), [locationId, runJsonMutation]);

  const createContact = useCallback(async (payload) => runJsonMutation(
    `/api/locations/profiles/${locationId}/contacts`,
    'POST',
    payload,
    'Failed to create contact'
  ), [locationId, runJsonMutation]);

  const updateContact = useCallback(async (contactId, payload) => runJsonMutation(
    `/api/locations/contacts/${contactId}`,
    'PUT',
    payload,
    'Failed to update contact'
  ), [runJsonMutation]);

  const deleteContact = useCallback(async (contactId) => runMutation(
    `/api/locations/contacts/${contactId}`,
    { method: 'DELETE' },
    'Failed to delete contact'
  ), [runMutation]);

  const createTicketLink = useCallback(async (payload) => runJsonMutation(
    `/api/locations/profiles/${locationId}/ticket-links`,
    'POST',
    payload,
    'Failed to create ticket link'
  ), [locationId, runJsonMutation]);

  const deleteTicketLink = useCallback(async (linkId) => runMutation(
    `/api/locations/ticket-links/${linkId}`,
    { method: 'DELETE' },
    'Failed to delete ticket link'
  ), [runMutation]);

  const deleteAsset = useCallback(async (assetId) => runMutation(
    `/api/locations/assets/${assetId}`,
    { method: 'DELETE' },
    'Failed to delete asset'
  ), [runMutation]);

  const setPrimaryAsset = useCallback(async (assetId) => runMutation(
    `/api/locations/assets/${assetId}/primary`,
    { method: 'PUT' },
    'Failed to set location photo'
  ), [runMutation]);

  const uploadAsset = useCallback(async ({ file, assetType, visibility, caption, isPrimary }) => {
    const body = new FormData();
    body.append('asset', file);
    body.append('assetType', assetType);
    body.append('visibility', visibility);
    body.append('caption', caption || '');
    if (isPrimary) body.append('isPrimary', 'true');
    const response = await authenticatedFetch(`/api/locations/profiles/${locationId}/assets`, { method: 'POST', body });
    const data = await readJson(response);
    if (!response.ok) throw new Error(data.error || 'Failed to upload asset');
    await reload();
    return data;
  }, [authenticatedFetch, locationId, reload]);

  return Object.assign({}, state, {
    reload,
    uploadAsset,
    updateProfile,
    createContact,
    updateContact,
    deleteContact,
    createTicketLink,
    deleteTicketLink,
    deleteAsset,
    setPrimaryAsset
  });
};
